import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Avatar,
  Fade,
  InputAdornment,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { login, isAuthenticated } from "../utils/auth";
import CatLoginIcon from "../assets/icons/CatLoginIcon.png";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  PersonOutline,
} from "@mui/icons-material";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/articles");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const success = await login(username, password);
      if (success) {
        navigate("/articles"); // Redirect to dashboard or home page
      } else {
        setError("Login failed: Invalid credentials");
      }
    } catch (error) {
      console.error("Login error details:", error);
      setError(
        error.message || "Login failed. Please check console for details."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
        overflow: "hidden",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: { xs: 2, sm: 8 },
          px: { xs: 2, sm: 0 },
          maxWidth: { xs: "100%", sm: "450px" },
          mx: "auto",
        }}
      >
        <Fade in={true} timeout={1000}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2, sm: 5 },
              width: "100%",
              maxWidth: { xs: "95%", sm: "100%" },
              borderRadius: { xs: "12px", sm: "20px" },
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              overflow: "hidden",
              position: "relative",
              backdropFilter: "blur(10px)",
              background: "rgba(255, 255, 255, 0.95)",
              transition: "box-shadow 0.3s ease-in-out",
              "&:hover": {
                boxShadow: "0 20px 45px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: { xs: 2, sm: 4.5 },
              }}
            >
              <Avatar
                src={CatLoginIcon}
                alt="Login"
                sx={{
                  width: { xs: 60, sm: 100 },
                  height: { xs: 60, sm: 100 },
                  mb: { xs: 1.5, sm: 3 },
                  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
                  border: "4px solid white",
                }}
              />
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontWeight: 800,
                  color: "#1a1a2e",
                  letterSpacing: "-0.5px",
                  mb: 1,
                  textShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                  background:
                    "linear-gradient(45deg, #1a1a2e 30%, #323232 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "1.5rem", sm: "2.125rem" },
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                sx={{
                  mt: 0.5,
                  maxWidth: "90%",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "#666",
                  lineHeight: 1.5,
                }}
              >
                Log in to access your content dashboard
              </Typography>
            </Box>

            {error && (
              <Fade in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(211, 47, 47, 0.1)",
                    border: "1px solid rgba(211, 47, 47, 0.2)",
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline
                        sx={{
                          color: "#666",
                          fontSize: isMobile ? "1.2rem" : "1.5rem",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    height: { xs: "45px", sm: "56px" },
                    "&:hover fieldset": {
                      borderColor: "#323232",
                    },
                    "& fieldset": {
                      borderWidth: "1.5px",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#323232",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 500,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.9rem", sm: "1.05rem" },
                    padding: { xs: "0 10px", sm: "0 14px" },
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined
                        sx={{
                          color: "#666",
                          fontSize: isMobile ? "1.2rem" : "1.5rem",
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: "#666",
                          padding: isMobile ? "4px" : "8px",
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOff
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        ) : (
                          <Visibility
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: { xs: 3, sm: 4 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    height: { xs: "45px", sm: "56px" },
                    "&:hover fieldset": {
                      borderColor: "#323232",
                    },
                    "& fieldset": {
                      borderWidth: "1.5px",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#323232",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 500,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.9rem", sm: "1.05rem" },
                    padding: { xs: "0 10px", sm: "0 14px" },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  mb: 2,
                  py: { xs: 1.2, sm: 2 },
                  textTransform: "none",
                  fontSize: { xs: "0.95rem", sm: "1.15rem" },
                  fontWeight: 600,
                  borderRadius: "14px",
                  background:
                    "linear-gradient(45deg, #323232 30%, #444444 90%)",
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                  transition:
                    "background 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #222222 30%, #333333 90%)",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.2)",
                  },
                  height: { xs: "45px", sm: "auto" },
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={isMobile ? 20 : 26} color="inherit" />
                ) : (
                  "Log In"
                )}
              </Button>

              <Divider sx={{ my: { xs: 1.5, sm: 2 }, opacity: 0.6 }}></Divider>

              <Typography
                variant="caption"
                align="center"
                sx={{
                  display: "block",
                  color: "#888",
                  mt: 1,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                © {new Date().getFullYear()} Content Dashboard
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </Container>
  );
}

export default Login;
